'use client';

import { runtimeConfig } from '@/config/runtime-config';
import { shouldRequestPhoneNumberOnStart } from '@/utils/telegram-phone-request';
import {
  telegramUsersGetMeQueryKey,
  telegramUsersGetMeQueryOptions,
  useTelegramAuthTelegramAuthorizeByTelegramData,
} from '@/generated';
import { useRouter } from '@/hooks/useRouter';
import { usePathname } from '@/navigation';
import { clearClientAuthCookies, writeClientAuthCookies } from '@/utils/auth-cookies';
import { isAccountBlockedError } from '@/utils/auth-errors';
import {
  bootstrapTelegramMiniAppAuth,
  getTelegramMiniAppRedirectTarget,
  TelegramMiniAppAuthError,
} from '@/utils/telegram-mini-app';
import { IconX } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

type TelegramWebApp = {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
  openTelegramLink?: (url: string) => void;
  openInvoice?: (url: string, callback?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void) => void;
  requestContact?: () => void;
  onEvent?: (eventType: 'contactRequested', eventHandler: (payload: { status: 'sent' | 'cancelled' }) => void) => void;
  offEvent?: (eventType: 'contactRequested', eventHandler: (payload: { status: 'sent' | 'cancelled' }) => void) => void;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };
  HapticFeedback?: {
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
};

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

type PhoneRequestStatus = 'idle' | 'cancelled' | 'failed';

type TelegramMiniAppContextValue = {
  isMiniAppMode: boolean;
  isTelegramEnvironment: boolean;
  isBootstrapping: boolean;
  authError: TelegramMiniAppAuthError | null;
  isPhoneRequired: boolean;
  isRequestingPhone: boolean;
  requestPhoneContact: () => Promise<string | undefined>;
  dismissPhoneRequest: () => void;
};

type TelegramMiniAppState = Omit<TelegramMiniAppContextValue, 'requestPhoneContact' | 'dismissPhoneRequest'>;

const defaultState: TelegramMiniAppState = {
  isMiniAppMode: runtimeConfig.isTelegramMiniApp,
  isTelegramEnvironment: false,
  isBootstrapping: runtimeConfig.isTelegramMiniApp,
  authError: null,
  isPhoneRequired: false,
  isRequestingPhone: false,
};

const defaultValue: TelegramMiniAppContextValue = {
  ...defaultState,
  requestPhoneContact: async () => undefined,
  dismissPhoneRequest: () => {},
};

const TelegramMiniAppContext = createContext<TelegramMiniAppContextValue>(defaultValue);
const loginUserQueryKey = ['get-me-once-unique'];
const clearLoginSearchParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('login');
  url.searchParams.delete('redirect');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

const isUnauthorizedError = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 401;

const phoneRequestBenefits = [
  {
    icon: '/assets/icons/tg-message-icon.svg',
    key: 'sellerContact',
  },
  {
    icon: '/assets/icons/tg-document-icon.svg',
    key: 'listingPublishing',
  },
  {
    icon: '/assets/icons/tg-goal-icon.svg',
    key: 'fullAccess',
  },
] as const;

function TelegramMiniAppShell({
  authError,
  isPhoneRequired,
  isRequestingPhone,
  onDismissPhone,
  onRequestPhone,
  phoneRequestStatus,
  telegramBotUsername,
}: {
  authError: TelegramMiniAppAuthError | null;
  isPhoneRequired: boolean;
  isRequestingPhone: boolean;
  onDismissPhone: () => void;
  onRequestPhone: () => void;
  phoneRequestStatus: PhoneRequestStatus;
  telegramBotUsername?: string;
}) {
  const t = useTranslations('modals.telegramMiniApp');
  const botUrl = telegramBotUsername ? `https://t.me/${telegramBotUsername}?startapp` : null;
  const phoneStatusMessage =
    phoneRequestStatus === 'cancelled'
      ? t('phone.status.cancelled')
      : phoneRequestStatus === 'failed'
        ? t('phone.status.failed')
        : null;
  const content = authError
    ? (() => {
        if (authError === 'telegram_environment_unavailable') {
          return {
            title: t('auth.telegramEnvironmentUnavailable.title'),
            description: t('auth.telegramEnvironmentUnavailable.description'),
          };
        }

        if (authError === 'account_blocked') {
          return {
            title: t('auth.accountBlocked.title'),
            description: t('auth.accountBlocked.description'),
          };
        }

        if (authError === 'telegram_auth_unavailable') {
          return {
            title: t('auth.telegramAuthUnavailable.title'),
            description: t('auth.telegramAuthUnavailable.description'),
          };
        }

        return {
          title: t('auth.telegramAuthFailed.title'),
          description: t('auth.telegramAuthFailed.description', { error: authError }),
        };
      })()
    : isPhoneRequired
      ? {
          title: t('phone.title'),
          description:
            phoneRequestStatus === 'cancelled'
              ? t('phone.descriptionCancelled')
              : phoneRequestStatus === 'failed'
                ? t('phone.descriptionFailed')
                : t('phone.descriptionShort'),
        }
      : {
          title: t('connecting.title'),
          description: t('connecting.description'),
        };
  const shouldShowSpinner = (!authError && !isPhoneRequired) || isRequestingPhone;

  if (isPhoneRequired && !authError) {
    return (
      <div className='fixed inset-0 z-[200] flex min-h-[100svh] items-center justify-center bg-[#19201C] bg-opacity-20 px-[19px] py-[calc(env(safe-area-inset-top)_+_24px)] pb-[calc(env(safe-area-inset-bottom)_+_24px)]'>
        <div className='relative flex w-full max-w-[338px] flex-col items-center rounded-[21px] bg-white px-[24px] pb-[24px] pt-[34px] text-center shadow-[0_24px_70px_rgba(0,0,0,0.24)]'>
          {!isRequestingPhone && (
            <Button
              variant='secondary'
              size='icon'
              onClick={onDismissPhone}
              aria-label={t('close')}
              className='absolute end-[8px] top-[8px] size-[40px] rounded-full bg-[#E8EBF1] text-text-primary-day hover:bg-[#E8EBF1]/90'
            >
              <IconX className='size-[22px]' stroke={1.8} />
            </Button>
          )}

          <Image
            src='/assets/images/tg_share_number.png'
            alt=''
            width={121}
            height={100}
            className='mb-[24px] h-[100px] w-[121px] object-contain'
            aria-hidden='true'
            priority
          />

          <h2 className='mb-[8px] max-w-[288px] text-[18px] font-[700] leading-[22px] text-text-primary-day'>
            {t('phone.title')}
          </h2>
          <p className='max-w-[300px] text-[14px] font-[400] leading-[18px] text-text-disabled-day'>
            {t('phone.description')}
          </p>
          {phoneStatusMessage && (
            <p className='mt-[10px] max-w-[286px] text-[13px] leading-[16px] text-red-day'>{phoneStatusMessage}</p>
          )}

          <div className='mt-[12px] flex w-full flex-col gap-[8px] text-start'>
            {phoneRequestBenefits.map((benefit) => (
              <div key={benefit.icon} className='grid grid-cols-[26px_1fr] items-center gap-[10px]'>
                <span className='flex size-[28px] items-center justify-center rounded-full bg-bg2-day'>
                  <Image
                    src={benefit.icon}
                    alt=''
                    width={20}
                    height={20}
                    className='max-h-[20px] max-w-[20px]'
                    aria-hidden='true'
                  />
                </span>
                <span className='text-[14px] font-[400] leading-[17px] text-text-primary-day'>
                  {t(`phone.benefits.${benefit.key}`)}
                </span>
              </div>
            ))}
          </div>

          <Button
            onClick={onRequestPhone}
            disabled={isRequestingPhone}
            className='mt-[18px] min-h-[44px] w-full rounded-[11px] px-[16px] text-[14px] font-[600] leading-[17px]'
          >
            {isRequestingPhone ? t('phone.waiting') : t('phone.shareButton')}
          </Button>
          {!isRequestingPhone && (
            <Button
              variant='link'
              onClick={onDismissPhone}
              className='mt-[18px] min-h-[20px] h-auto p-0 text-[14px] font-[600] leading-[17px] text-primary-green-day no-underline hover:no-underline'
            >
              {t('phone.notNow')}
            </Button>
          )}

          <div className='mt-[36px] grid min-h-[49px] w-full grid-cols-[24px_1fr] items-center gap-[12px] rounded-[15px] bg-[#F4F5FA] px-[12px] py-[9px] text-start'>
            <Image
              src='/assets/icons/tg-lock-icon.svg'
              alt=''
              width={24}
              height={24}
              className='size-[24px]'
              aria-hidden='true'
            />
            <p className='text-[12px] font-[600] leading-[14px] text-text-disabled-day'>{t('phone.lockNote')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='fixed inset-0 z-200 flex min-h-svh items-center justify-center bg-bg-day px-[20px] py-[calc(env(safe-area-inset-top)+24px)] pb-[calc(env(safe-area-inset-bottom)+24px)]'>
      <div className='relative flex w-full max-w-[360px] flex-col items-center gap-[16px] rounded-[24px] bg-white px-[24px] py-[28px] text-center shadow-[0_20px_60px_rgba(0,0,0,0.12)]'>
        {isPhoneRequired && !authError && !isRequestingPhone && (
          <Button
            variant='ghost'
            size='icon'
            onClick={onDismissPhone}
            aria-label={t('close')}
            className='absolute end-[12px] top-[12px] size-[32px] rounded-full text-text-disabled-day'
          >
            <IconX className='size-[20px]' />
          </Button>
        )}
        {shouldShowSpinner ? (
          <Spinner className='size-[28px] text-primary-green-day' />
        ) : (
          <div className='size-[56px] rounded-full border border-solid border-primary-stroke-day bg-bg2-day' />
        )}
        <div className='flex flex-col gap-[8px]'>
          <h2 className='text-[20px] font-semibold text-text-primary-day'>{content.title}</h2>
          <p className='text-s text-text-disabled-day'>{content.description}</p>
        </div>
        {isPhoneRequired && !authError && (
          <Button
            onClick={onRequestPhone}
            disabled={isRequestingPhone}
            className='min-h-[44px] w-full rounded-[12px] px-[16px] text-m'
          >
            {isRequestingPhone ? t('phone.waiting') : t('phone.shareButton')}
          </Button>
        )}
        {authError && botUrl && (
          <a
            href={botUrl}
            target='_blank'
            rel='noreferrer'
            className='flex min-h-[44px] w-full items-center justify-center rounded-[12px] bg-primary-green-day px-[16px] text-m text-white'
          >
            {t('auth.openInTelegram')}
          </a>
        )}
      </div>
    </div>
  );
}

export function TelegramMiniAppProvider({
  children,
  telegramBotUsername,
}: {
  children: ReactNode;
  telegramBotUsername?: string;
}) {
  const isMiniAppMode = runtimeConfig.isTelegramMiniApp;
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const didBootstrapRef = useRef(false);
  const initialPathnameRef = useRef(pathname);
  const [state, setState] = useState<TelegramMiniAppState>(defaultState);
  const [phoneRequestStatus, setPhoneRequestStatus] = useState<PhoneRequestStatus>('idle');
  const { mutateAsync: authorizeByTelegram } = useTelegramAuthTelegramAuthorizeByTelegramData({
    client: {
      handle401: false,
    },
  });

  useEffect(() => {
    if (!isMiniAppMode) {
      return;
    }

    const backButton = window.Telegram?.WebApp?.BackButton;
    if (!backButton) {
      return;
    }

    if (pathname === '/') {
      backButton.hide();
      return;
    }

    const handleBack = () => {
      if (pathname === initialPathnameRef.current) {
        router.replace('/');
        return;
      }

      router.back();
    };

    backButton.show();
    backButton.onClick(handleBack);

    return () => {
      backButton.offClick(handleBack);
    };
  }, [isMiniAppMode, pathname, router]);

  const handlePostLoginRedirect = useCallback(() => {
    const redirectTarget = getTelegramMiniAppRedirectTarget(new URL(window.location.href).searchParams.get('redirect'));

    if (!redirectTarget) {
      clearLoginSearchParams();
      return;
    }

    router.replace(redirectTarget);
  }, [router]);

  const fetchMiniAppUser = useCallback(async () => {
    const me = await queryClient.fetchQuery({
      ...telegramUsersGetMeQueryOptions({
        handle401: false,
      }),
      staleTime: 0,
    });

    queryClient.setQueryData(loginUserQueryKey, me);
    return me;
  }, [queryClient]);

  const clearMiniAppAuthState = useCallback(() => {
    clearClientAuthCookies();
    queryClient.setQueryData(loginUserQueryKey, null);
    queryClient.removeQueries({ queryKey: loginUserQueryKey, exact: true });
    queryClient.removeQueries({ queryKey: telegramUsersGetMeQueryKey(), exact: true });
  }, [queryClient]);

  const authorizeWithTelegramInitData = useCallback(
    async (initData: string) =>
      authorizeByTelegram({
        data: {
          data: initData,
        },
      }),
    [authorizeByTelegram],
  );

  const recoverMiniAppAuth = useCallback(
    async ({ initData, isTelegramEnvironment }: { initData?: string | null; isTelegramEnvironment: boolean }) => {
      clearMiniAppAuthState();

      const result = await bootstrapTelegramMiniAppAuth({
        cookieString: document.cookie,
        forceLogin: true,
        initData,
        isTelegramEnvironment,
        authorize: authorizeWithTelegramInitData,
        persistTokens: async (tokens) => {
          writeClientAuthCookies(tokens);
        },
      });

      if (result.status === 'blocked') {
        clearMiniAppAuthState();
        return result;
      }

      let me;
      try {
        me = await fetchMiniAppUser();
      } catch (error) {
        clearMiniAppAuthState();
        return {
          status: 'blocked' as const,
          error: isAccountBlockedError(error) ? ('account_blocked' as const) : ('telegram_auth_failed' as const),
        };
      }

      return {
        status: 'ready' as const,
        performedLogin: result.performedLogin,
        me,
      };
    },
    [authorizeWithTelegramInitData, clearMiniAppAuthState, fetchMiniAppUser],
  );

  const requestPhoneContact = useCallback(async () => {
    if (state.isRequestingPhone) {
      return;
    }

    const webApp = window.Telegram?.WebApp;
    if (!webApp?.requestContact || !webApp?.onEvent) {
      setPhoneRequestStatus('failed');
      setState((current) => ({
        ...current,
        isPhoneRequired: true,
        isRequestingPhone: false,
      }));
      return;
    }

    const { onEvent, offEvent, requestContact } = webApp;

    setPhoneRequestStatus('idle');
    setState((current) => ({
      ...current,
      isPhoneRequired: true,
      isRequestingPhone: true,
    }));

    try {
      const status = await new Promise<'sent' | 'cancelled'>((resolve, reject) => {
        const handleContactRequested = ({ status: nextStatus }: { status: 'sent' | 'cancelled' }) => {
          offEvent?.('contactRequested', handleContactRequested);
          resolve(nextStatus);
        };

        onEvent('contactRequested', handleContactRequested);

        try {
          requestContact();
        } catch (error) {
          offEvent?.('contactRequested', handleContactRequested);
          reject(error);
        }
      });

      if (status !== 'sent') {
        setPhoneRequestStatus('cancelled');
        setState((current) => ({
          ...current,
          isPhoneRequired: true,
          isRequestingPhone: false,
        }));
        return undefined;
      }

      const webApp = window.Telegram?.WebApp;
      const isTelegramEnvironment = !!webApp;
      let me;

      try {
        me = await fetchMiniAppUser();
      } catch (error) {
        if (!isUnauthorizedError(error)) {
          throw error;
        }

        const recovery = await recoverMiniAppAuth({
          initData: webApp?.initData,
          isTelegramEnvironment,
        });

        if (recovery.status === 'blocked') {
          setState((current) => ({
            ...current,
            authError: recovery.error,
            isPhoneRequired: false,
            isRequestingPhone: false,
          }));
          setPhoneRequestStatus('idle');
          return undefined;
        }

        me = recovery.me;
      }

      const hasPhoneNumber = !!me?.phoneNumber;

      setPhoneRequestStatus(hasPhoneNumber ? 'idle' : 'failed');
      setState((current) => ({
        ...current,
        isPhoneRequired: !hasPhoneNumber,
        isRequestingPhone: false,
      }));
      return me?.phoneNumber ?? undefined;
    } catch (error) {
      console.error('Failed to request Telegram contact', error);
      setPhoneRequestStatus('failed');
      setState((current) => ({
        ...current,
        isPhoneRequired: true,
        isRequestingPhone: false,
      }));
      return undefined;
    }
  }, [fetchMiniAppUser, recoverMiniAppAuth, state.isRequestingPhone]);

  const dismissPhoneRequest = useCallback(() => {
    setPhoneRequestStatus('idle');
    setState((current) => ({
      ...current,
      isPhoneRequired: false,
      isRequestingPhone: false,
    }));
  }, []);

  useEffect(() => {
    if (!isMiniAppMode) {
      setState({
        isMiniAppMode: false,
        isTelegramEnvironment: false,
        isBootstrapping: false,
        authError: null,
        isPhoneRequired: false,
        isRequestingPhone: false,
      });
      setPhoneRequestStatus('idle');
      return;
    }

    if (didBootstrapRef.current) {
      return;
    }
    didBootstrapRef.current = true;

    const webApp = window.Telegram?.WebApp;
    const isTelegramEnvironment = !!webApp;

    webApp?.ready?.();
    webApp?.expand?.();

    let isCancelled = false;

    void (async () => {
      const result = await bootstrapTelegramMiniAppAuth({
        cookieString: document.cookie,
        initData: webApp?.initData,
        isTelegramEnvironment,
        authorize: authorizeWithTelegramInitData,
        persistTokens: async (tokens) => {
          writeClientAuthCookies(tokens);
        },
      });

      if (isCancelled) {
        return;
      }

      if (result.status === 'blocked') {
        clearMiniAppAuthState();
        setState({
          isMiniAppMode: true,
          isTelegramEnvironment,
          isBootstrapping: false,
          authError: result.error,
          isPhoneRequired: false,
          isRequestingPhone: false,
        });
        setPhoneRequestStatus('idle');
        return;
      }

      try {
        const me = await fetchMiniAppUser();

        if (isCancelled) {
          return;
        }

        const isPhoneRequired = shouldRequestPhoneNumberOnStart(
          runtimeConfig.isPhoneRequestOnStartEnabled,
          !!me?.phoneNumber,
          window.localStorage,
        );

        setState({
          isMiniAppMode: true,
          isTelegramEnvironment,
          isBootstrapping: false,
          authError: null,
          isPhoneRequired,
          isRequestingPhone: false,
        });
        setPhoneRequestStatus('idle');
      } catch (error) {
        console.error('Failed to load Telegram Mini App user', error);

        if (isCancelled) {
          return;
        }

        if (isUnauthorizedError(error)) {
          const recovery = await recoverMiniAppAuth({
            initData: webApp?.initData,
            isTelegramEnvironment,
          });

          if (isCancelled) {
            return;
          }

          if (recovery.status !== 'blocked') {
            const isPhoneRequired = shouldRequestPhoneNumberOnStart(
              runtimeConfig.isPhoneRequestOnStartEnabled,
              !!recovery.me?.phoneNumber,
              window.localStorage,
            );

            setState({
              isMiniAppMode: true,
              isTelegramEnvironment,
              isBootstrapping: false,
              authError: null,
              isPhoneRequired,
              isRequestingPhone: false,
            });
            setPhoneRequestStatus('idle');

            if (result.performedLogin || recovery.performedLogin) {
              handlePostLoginRedirect();
            }
            return;
          }

          clearMiniAppAuthState();
          setState({
            isMiniAppMode: true,
            isTelegramEnvironment,
            isBootstrapping: false,
            authError: recovery.error,
            isPhoneRequired: false,
            isRequestingPhone: false,
          });
          setPhoneRequestStatus('idle');
          return;
        }

        clearMiniAppAuthState();
        setState({
          isMiniAppMode: true,
          isTelegramEnvironment,
          isBootstrapping: false,
          authError: isAccountBlockedError(error) ? 'account_blocked' : 'telegram_auth_failed',
          isPhoneRequired: false,
          isRequestingPhone: false,
        });
        setPhoneRequestStatus('idle');
        return;
      }

      if (result.performedLogin) {
        handlePostLoginRedirect();
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    authorizeWithTelegramInitData,
    clearMiniAppAuthState,
    fetchMiniAppUser,
    handlePostLoginRedirect,
    isMiniAppMode,
    recoverMiniAppAuth,
  ]);

  const contextValue = useMemo(
    () => ({
      ...state,
      requestPhoneContact,
      dismissPhoneRequest,
    }),
    [dismissPhoneRequest, requestPhoneContact, state],
  );

  return (
    <TelegramMiniAppContext.Provider value={contextValue}>
      {state.isMiniAppMode && (state.isBootstrapping || state.authError) ? (
        <TelegramMiniAppShell
          authError={state.authError}
          isPhoneRequired={state.isPhoneRequired}
          isRequestingPhone={state.isRequestingPhone}
          onDismissPhone={dismissPhoneRequest}
          onRequestPhone={() => {
            void requestPhoneContact();
          }}
          phoneRequestStatus={phoneRequestStatus}
          telegramBotUsername={telegramBotUsername}
        />
      ) : (
        <>
          {children}
          {state.isMiniAppMode && state.isPhoneRequired && (
            <TelegramMiniAppShell
              authError={state.authError}
              isPhoneRequired={state.isPhoneRequired}
              isRequestingPhone={state.isRequestingPhone}
              onDismissPhone={dismissPhoneRequest}
              onRequestPhone={() => {
                void requestPhoneContact();
              }}
              phoneRequestStatus={phoneRequestStatus}
              telegramBotUsername={telegramBotUsername}
            />
          )}
        </>
      )}
    </TelegramMiniAppContext.Provider>
  );
}

export const useTelegramMiniApp = () => useContextSelector(TelegramMiniAppContext, (value) => value);
